import { useCallback, useEffect, useState } from 'react';
import { pdApi, type PdProject, type PdUser } from '../api';
import '../screens.css';
import { HelpPanel } from '../HelpPanel';
import { PROJECT_ADMINISTRATION } from '../helpContent';

/**
 * Projects → Project Administration.
 *
 * Cloned from `views/project-administration.ejs`.
 *
 * WHO CAN GRANT: an administrator, or someone who is Owner or Project Manager on the
 * project in question. A non-administrator sees only the projects they can actually grant
 * on, which is why the server returns `grantable_projects` separately from `projects`.
 *
 * OWNER IS NOT IN THE GRANTABLE LIST. Ownership comes from creating the project and is not
 * handed out through this screen — the server rejects it. Revoking an existing Owner is
 * administrator-only, so that button is gated rather than hidden: a Project Manager should
 * see that the row exists and why they cannot remove it.
 */

type Dict = Record<string, unknown>;

const GRANTABLE = [
  'Full',
  'Read-Only',
  'Project Manager',
  'Project Team Member',
  'Key Stakeholder',
  'SteerCo',
] as const;

export function ProjectAdministration() {
  const [grantable, setGrantable] = useState<PdProject[]>([]);
  const [users, setUsers] = useState<PdUser[]>([]);
  const [records, setRecords] = useState<Dict[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [granting, setGranting] = useState<Dict | null>(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pdApi.projects.administration();
      setGrantable(data.grantable_projects);
      setUsers(data.users);
      setRecords(data.access_records);
      setIsAdmin(data.isAdmin);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (ok) setNotice(ok);
      setGranting(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const shown = records.filter((r) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      String(r.project_id).toLowerCase().includes(q) ||
      String(r.user_full_name ?? '').toLowerCase().includes(q) ||
      String(r.user_email ?? '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">Project Administration</h1>
          <p className="pd-page-header__subtitle">
            Who can see and work on each project. {isAdmin ? '' : 'You see only the projects you own or manage.'}
          </p>
        </div>
        <div className="pd-page-header__actions">
          <span className="pd-count">{records.length} grants</span>
          <button
            type="button"
            className="pd-btn pd-btn--primary"
            disabled={grantable.length === 0}
            title={grantable.length === 0 ? 'You cannot grant access on any project' : undefined}
            onClick={() =>
              setGranting({
                project_id: grantable[0]?.project_id ?? '',
                user_id: users[0]?.user_id ?? '',
                access_type: 'Read-Only',
                notes: '',
              })
            }
          >
            Grant access
          </button>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={PROJECT_ADMINISTRATION} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}
      {notice ? <div className="pd-notice">{notice}</div> : null}

      <div className="pd-filters">
        <input
          type="text"
          placeholder="Filter by project, name or email…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="pd-loading">Loading…</div>
      ) : (
        <div className="pd-table-wrap">
          <table className="pd-table">
            <thead>
              <tr>
                <th style={{ width: 150 }}>Project</th>
                <th style={{ width: 190 }}>User</th>
                <th style={{ width: 220 }}>Email</th>
                <th style={{ width: 170 }}>Access</th>
                <th style={{ width: 160 }}>Granted by</th>
                <th>Notes</th>
                <th style={{ width: 80 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 ? (
                <tr>
                  <td colSpan={7} className="pd-empty">
                    {records.length === 0
                      ? 'No access has been granted yet. Project creators are Owners automatically.'
                      : 'No grants match this filter.'}
                  </td>
                </tr>
              ) : (
                shown.map((r) => {
                  const isOwner = r.access_type === 'Owner';
                  return (
                    <tr key={String(r.access_id)}>
                      <td>
                        <div>{String(r.project_id)}</div>
                        <div className="pd-hint">{String(r.project_description ?? '')}</div>
                      </td>
                      <td>{String(r.user_full_name ?? '')}</td>
                      <td>{String(r.user_email ?? '')}</td>
                      <td>
                        <span
                          className="pd-badge"
                          style={{ color: isOwner ? '#1e40af' : '#475569' }}
                        >
                          {String(r.access_type)}
                        </span>
                      </td>
                      <td>{String(r.granted_by_name ?? '—')}</td>
                      <td>{String(r.notes ?? '')}</td>
                      <td>
                        <div className="pd-row-actions">
                          <button
                            type="button"
                            className="pd-icon-btn"
                            title={
                              isOwner && !isAdmin
                                ? 'Only an administrator can revoke Owner access'
                                : 'Revoke'
                            }
                            disabled={isOwner && !isAdmin}
                            onClick={() => {
                              if (
                                !window.confirm(
                                  `Revoke ${String(r.access_type)} on ${String(r.project_id)} from ${String(r.user_full_name)}?`,
                                )
                              )
                                return;
                              return run(
                                () => pdApi.projects.revokeAccess(Number(r.access_id)),
                                'Access revoked',
                              );
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {granting ? (
        <div className="pd-modal-backdrop" onClick={() => setGranting(null)}>
          <div className="pd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal__head">
              <h2 className="pd-modal__title">Grant project access</h2>
              <button type="button" className="pd-icon-btn" onClick={() => setGranting(null)}>
                ✕
              </button>
            </div>
            <div className="pd-modal__body">
              <div className="pd-field">
                <label htmlFor="pa-project">Project *</label>
                <select
                  id="pa-project"
                  value={String(granting.project_id ?? '')}
                  onChange={(e) => setGranting({ ...granting, project_id: e.target.value })}
                >
                  {grantable.map((p) => (
                    <option key={p.project_id} value={p.project_id}>
                      {p.project_id} — {p.project_description}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pd-field">
                <label htmlFor="pa-user">User *</label>
                <select
                  id="pa-user"
                  value={String(granting.user_id ?? '')}
                  onChange={(e) => setGranting({ ...granting, user_id: e.target.value })}
                >
                  {users.map((u) => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.full_name} — {u.email}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pd-field">
                <label htmlFor="pa-type">Access type *</label>
                <select
                  id="pa-type"
                  value={String(granting.access_type ?? '')}
                  onChange={(e) => setGranting({ ...granting, access_type: e.target.value })}
                >
                  {GRANTABLE.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
                <span className="pd-hint">
                  Owner is not grantable — it comes from creating the project.
                </span>
              </div>
              <div className="pd-field">
                <label htmlFor="pa-notes">Notes</label>
                <textarea
                  id="pa-notes"
                  value={String(granting.notes ?? '')}
                  onChange={(e) => setGranting({ ...granting, notes: e.target.value })}
                />
              </div>
            </div>
            <div className="pd-modal__foot">
              <button type="button" className="pd-btn" onClick={() => setGranting(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="pd-btn pd-btn--primary"
                onClick={() =>
                  run(() => pdApi.projects.grantAccess(granting), 'Access granted')
                }
              >
                Grant
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
